import React, { useEffect, useRef, useState } from 'react';
import { Icon, InputField, Loader, Typography } from '@saviynt/design-system';

import useGetUsers from '../../../hooks/use-getusers';
import { type User } from '../../../services/types';
import { isEmpty, useDebounce, useInvertedLocalize } from '../../../utils';
import UserSkeleton from '../UserSkeleton/UserSkeleton';

import UserCard from './UserCard/UserCard';
import invertedMessages from './messages';

import './UsersList.css';

interface UsersListProps {
  jobInProgress?: boolean;
  userType?: string;
  searchPlaceholder?: string;
  isJobNotStarted?: boolean;
  isDashboardDataLoading?: boolean;
}

const USERS_PER_PAGE = 10;
const SKELETON_COUNT = 10;

function UsersList({
  jobInProgress,
  userType,
  searchPlaceholder,
  isJobNotStarted,
  isDashboardDataLoading,
}: UsersListProps) {
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [lastElement, setLastElement] = useState<HTMLDivElement | null>(null);
  const [searchText, setSearchText] = useState('');
  const debouncedValue = useDebounce(searchText, 500);
  const l = useInvertedLocalize(invertedMessages);

  const {
    data,
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers,
    isFetched,
  } = useGetUsers({
    q: 'accountExpired:0',
    sortBy: 'username',
    userType,
    searchKeyword: debouncedValue,
    sortOrder: 'desc',
    page,
    size: USERS_PER_PAGE,
    enabled: page < totalPages,
  });

  const observer = useRef(
    new IntersectionObserver((entries) => {
      const firstEntry = entries[0];

      if (firstEntry.isIntersecting) {
        setPage((prevPage) => prevPage + 1);
      }
    })
  );

  useEffect(() => {
    const currentElement = lastElement;
    const currentObserver = observer.current;

    if (currentElement) {
      currentObserver.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement);
      }
    };
  }, [lastElement]);

  useEffect(() => {
    setPage(0);
    setAllUsers([]);
  }, [debouncedValue]);

  const isLoadingData =
    isFetched &&
    !isLoadingUsers &&
    !isFetchingUsers &&
    !isEmpty(data) &&
    data &&
    Number(data.totalItems) > 0;

  useEffect(() => {
    if (isLoadingData) {
      setAllUsers((oldUsers) => [...oldUsers, ...data.data]);
      setTotalPages(data.totalPages);
    }
  }, [isLoadingData, data]);

  return (
    <div className='Ext-UsersList'>
      {!isJobNotStarted &&
      !isDashboardDataLoading &&
      (allUsers.length || searchText) ? (
        <div className='Ext-UsersList-input'>
          <InputField
            name='basic'
            size='medium'
            placeholder={searchPlaceholder ?? l('Search identities')}
            kind='search'
            prefixIcon={
              <Icon kind='Search' color='neutral-600' size='smallMedium' />
            }
            value={searchText}
            setValue={setSearchText}
          />
        </div>
      ) : null}
      {!isDashboardDataLoading && jobInProgress ? (
        <div className='Ext-UsersList-skeletons'>
          {Array.from({ length: SKELETON_COUNT }).map(() => (
            <UserSkeleton />
          ))}
        </div>
      ) : (
        <>
          {!isJobNotStarted &&
            !isDashboardDataLoading &&
            allUsers.length > 0 &&
            allUsers.map((user: User) => (
              <div key={user.userKey} ref={setLastElement}>
                <UserCard user={user} />
              </div>
            ))}
          {isLoadingUsers && (
            <div className='Ext-UsersList-loader'>
              <Loader kind='loop' format='inline' color='brand' />
            </div>
          )}
          {!isLoadingUsers && allUsers.length < 1 && searchText ? (
            <Typography kind='body1'>{l('No users found')}</Typography>
          ) : null}
        </>
      )}
    </div>
  );
}

export default UsersList;

UsersList.defaultProps = {
  jobInProgress: false,
  userType: 'ALL',
  searchPlaceholder: 'Search Identities',
  isDashboardDataLoading: false,
  isJobNotStarted: false,
};
